import {
  Flex,
  GButton,
  GFeedbackModal,
  GModal,
  IconCart,
  IconCircleCheck,
  IconLocation,
} from '@gazin/ecommerce-ui'
import { NextRouter } from 'next/router'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useCartContext } from 'src/contexto/cartContext'
import {
  FeatureFlagSituationEnum,
  featureFlagEntidades,
} from 'src/services/marketplace-api'
import { Ad, FreightResponse } from 'src/services/marketplace-api/dtos'
import { theme } from 'src/styles/themes/default'
import { ProductCartData } from 'src/types/Carrinho'
import { checkProductQuantityExceeded } from 'src/util/checkProductQuantityExceeded'
import { CookieKeyTypeEnum, getCookieItem } from 'src/util/cookie-domain'
import { formatarPreco } from 'src/util/formatar'
import { removeChoosePickUp, saveChoosePickUp } from 'src/util/inStorePickUp'
import {
  isMultiVoltageSlug,
  isUniqueVoltageSlug,
} from 'src/util/productCombination'
import { AttributeProps } from '../../types'
import { getStyles } from './styles'
import { VoltageConfirmationModal } from './voltageConfirmationModal'

type BuyNowAndAddCartButtonsProps = {
  activeVariationAdId: number
  adActiveId: number
  ads: Ad[]
  dataProductCart?: ProductCartData
  existsSellers: boolean
  freight?: FreightResponse[]
  freightRef: React.MutableRefObject<any>
  hasWarranty: boolean
  productQuantity: number | string
  route: NextRouter
  setDataProductCart: Dispatch<SetStateAction<ProductCartData | undefined>>
  setShowError: Dispatch<SetStateAction<boolean>>
  shippingLoading: boolean
  uniqueVoltages: AttributeProps[]
  voltageProduct: { slug?: string; valor?: string }
}

export const BuyNowAndAddCartButtons: React.FC<
  BuyNowAndAddCartButtonsProps
> = ({
  activeVariationAdId,
  adActiveId,
  ads,
  dataProductCart,
  existsSellers,
  freight,
  freightRef,
  hasWarranty,
  productQuantity,
  route,
  setShowError,
  shippingLoading,
  uniqueVoltages,
  voltageProduct,
}) => {
  const carrinho = useCartContext()

  const [displayConfirmationVoltage, setDisplayConfirmationVoltage] =
    useState(false)
  const [isKeepBuying, setIsKeepBuying] = useState(false)
  const [isSelectedPickUp, setIsSelectedPickUp] = useState(false)
  const [loadingAddCartButton, setLoadingAddCartButton] = useState(false)
  const [loadingBuyNowButton, setLoadingBuyNowButton] = useState(false)
  const [showAddedProductModal, setShowAddedProductModal] = useState(false)

  const styles = getStyles()

  const pickUp = freight?.find((option) => !!option.lojas_retirada?.length)

  const isProductQuantityExceeded = checkProductQuantityExceeded(
    carrinho.carrinhoStore?.carrinhos_itens || [],
    adActiveId
  )

  const treatedAdId = existsSellers ? adActiveId : activeVariationAdId

  function goToWarrantyPage(
    adId: number,
    quantidade?: number,
    keepBuying?: boolean,
    selectedPickUp?: boolean
  ) {
    const pathname = `/garantia/${adId}`
    const query = { quantidade }

    if (keepBuying && !selectedPickUp) Object.assign(query, { keepBuying })

    if (selectedPickUp) Object.assign(query, { selectedPickUp })

    return route.push({ pathname, query })
  }

  async function addProductAndContinue(
    quantidade: number,
    anuncio_id: number,
    url: string,
    keepBuying?: boolean,
    selectedPickUp?: boolean
  ) {
    selectedPickUp && saveChoosePickUp()

    await carrinho.adicionar(quantidade, anuncio_id, url.split('?')[0])

    if (keepBuying && !selectedPickUp) {
      setDisplayConfirmationVoltage(false)
      setShowAddedProductModal(true)

      return setTimeout(() => {
        setShowAddedProductModal(false)
      }, 3000)
    }

    if (!keepBuying && !selectedPickUp) {
      removeChoosePickUp()
    }

    const checkoutPathname = `${process.env.NEXT_PUBLIC_CHECKOUT_APP_URI}`
    return route.push(
      {
        pathname: checkoutPathname,
        query: { isCompra: true },
      },
      checkoutPathname
    )
  }

  async function confirmVoltage() {
    const adId =
      isSelectedPickUp && pickUp?.id_anuncio ? pickUp.id_anuncio : treatedAdId

    if (hasWarranty) {
      setDisplayConfirmationVoltage(false)
      return goToWarrantyPage(
        adId,
        Number(productQuantity),
        isKeepBuying,
        isSelectedPickUp
      )
    }

    return addProductAndContinue(
      Number(productQuantity),
      adId,
      route.asPath,
      isKeepBuying,
      isSelectedPickUp
    )
  }

  async function addProductIntoCart(
    amount: number,
    adId: number,
    url: string,
    keepBuying = false,
    selectedPickUp = false
  ) {
    keepBuying ? setLoadingAddCartButton(true) : setLoadingBuyNowButton(true)

    setIsSelectedPickUp(selectedPickUp)
    setIsKeepBuying(keepBuying && !selectedPickUp)

    const featureFlagSmartDelivery = await featureFlagEntidades({
      featureFlag: 'smart_delivery',
    })
    let configSmartDelivery = false

    featureFlagSmartDelivery.configuracoes.map((config) => {
      if (config.situacao === FeatureFlagSituationEnum.ATIVO) {
        configSmartDelivery = true
      }
    })

    const validatedEntityActive =
      featureFlagSmartDelivery?.situacao === FeatureFlagSituationEnum.ATIVO

    if (
      !getCookieItem(CookieKeyTypeEnum.cep) &&
      !(configSmartDelivery && validatedEntityActive)
    ) {
      const message = 'Informe seu CEP'
      freightRef.current?.setError(message)
      keepBuying
        ? setLoadingAddCartButton(false)
        : setLoadingBuyNowButton(false)
      return toast.error(message)
    }

    if (isProductQuantityExceeded) {
      const message = 'Quantidade máxima atingida'
      keepBuying
        ? setLoadingAddCartButton(false)
        : setLoadingBuyNowButton(false)
      return toast.error(message)
    }

    if ((uniqueVoltages && !voltageProduct) || !voltageProduct.slug) {
      keepBuying
        ? setLoadingAddCartButton(false)
        : setLoadingBuyNowButton(false)
      setShowError(true)

      return document
        ?.querySelector('.opcoes-voltagem-produto')
        ?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
    }

    const hasUniqueVoltageSlug = isUniqueVoltageSlug(voltageProduct.slug)

    const hasUniqueVoltage =
      hasUniqueVoltageSlug || !uniqueVoltages || uniqueVoltages.length === 1

    const hasMultipleVoltageSlug = isMultiVoltageSlug(voltageProduct.slug)

    const hasMultipleVoltage =
      !hasUniqueVoltageSlug && uniqueVoltages && voltageProduct.slug

    if (hasUniqueVoltage && !hasMultipleVoltageSlug) {
      keepBuying
        ? setLoadingAddCartButton(false)
        : setLoadingBuyNowButton(false)

      if (hasWarranty)
        return goToWarrantyPage(adId, amount, keepBuying, selectedPickUp)

      return addProductAndContinue(
        amount,
        adId,
        url,
        keepBuying,
        selectedPickUp
      )
    }

    if (!!hasMultipleVoltage || hasMultipleVoltageSlug) {
      setLoadingAddCartButton(false)
      setLoadingBuyNowButton(false)
      setDisplayConfirmationVoltage(true)
    }
  }

  const onCloseConfirmVoltageModal = () => {
    setDisplayConfirmationVoltage(false)
    setLoadingAddCartButton(false)
    setLoadingBuyNowButton(false)
    setIsKeepBuying(false)
    setIsSelectedPickUp(false)
  }

  useEffect(() => {
    if (dataProductCart) {
      addProductIntoCart(
        dataProductCart.quantidade,
        dataProductCart.anuncio_id,
        dataProductCart.url
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProductCart])

  const buttonAddToCart = useMemo(() => {
    let label = 'Adicionar ao carrinho'
    let isPickUp = false

    if (pickUp) {
      isPickUp = true

      const isTheSameAdActive = pickUp?.id_anuncio === adActiveId

      const adActive = ads.find((ad) => ad.id === adActiveId)

      const adWithPickUp = ads.find((ad) => ad.id === pickUp?.id_anuncio)

      const itsNotTheSameAdActiveAndPricesAreDifferent =
        !isTheSameAdActive &&
        !!adWithPickUp &&
        !!adActive &&
        adWithPickUp.preco.por !== adActive.preco.por

      label = itsNotTheSameAdActiveAndPricesAreDifferent
        ? `Retirar por ${formatarPreco(adWithPickUp.preco.por)}`
        : 'Retirar'
    }

    return (
      <GButton
        _active={{
          bgColor: 'success.400',
          borderColor: 'success.400',
          color: 'white',
        }}
        _hover={{
          bgColor: 'success.500',
          color: 'white',
          svg: { color: 'white' },
        }}
        isDisabled={shippingLoading}
        isLoading={displayConfirmationVoltage ? false : loadingAddCartButton}
        bg="none"
        borderColor="success.500"
        borderRadius="0.25rem"
        color="success.500"
        height="3.375rem"
        label={label}
        leftIcon={
          isPickUp ? (
            <IconLocation
              color="success.500"
              h="1.5rem"
              marginRight="-1.5"
              w="1.5rem"
            />
          ) : undefined
        }
        onClick={() =>
          addProductIntoCart(
            Number(productQuantity),
            isPickUp && pickUp?.id_anuncio ? pickUp.id_anuncio : treatedAdId,
            route.asPath,
            true,
            isPickUp
          )
        }
        transition="all .3s"
        variantType="outline"
        width="100%"
      />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adActiveId,
    ads,
    displayConfirmationVoltage,
    loadingAddCartButton,
    pickUp,
    productQuantity,
    route.asPath,
    shippingLoading,
    treatedAdId,
  ])

  return (
    <>
      {showAddedProductModal && (
        <GFeedbackModal
          GProgressProps={{ ...styles.successModal }}
          icon={
            <IconCircleCheck
              color="success.500"
              h={{ base: '1.875rem', md: '3.625rem' }}
              w={{ base: '1.875rem', md: '3.625rem' }}
            />
          }
          setIsVisible={setShowAddedProductModal}
          timeToClose={2}
          title="Produto adicionado ao carrinho com sucesso!"
        />
      )}
      <GModal
        isOpen={displayConfirmationVoltage}
        modalBody={
          <VoltageConfirmationModal
            confirmVoltage={confirmVoltage}
            setDisplayConfirmationVoltage={setDisplayConfirmationVoltage}
            voltageProduct={voltageProduct}
            onClose={onCloseConfirmVoltageModal}
          />
        }
        onClose={() => {
          setDisplayConfirmationVoltage(false)
          setLoadingAddCartButton(false)
          setLoadingBuyNowButton(false)
        }}
        propsModal={{ isCentered: true, size: 'lg' }}
        styleHeader="clean"
      />
      <Flex direction="column" gap="1rem" mt="1.5rem">
        <GButton
          borderRadius="0.25rem"
          height="3.375rem"
          isLoading={displayConfirmationVoltage ? false : loadingBuyNowButton}
          label="Comprar agora"
          leftIcon={
            <IconCart
              color={theme.colors.white}
              height="1.1rem"
              width="1.1rem"
            />
          }
          onClick={() =>
            addProductIntoCart(
              Number(productQuantity),
              treatedAdId,
              route.asPath
            )
          }
          variant="success"
          width="100%"
        />

        {buttonAddToCart}
      </Flex>
    </>
  )
}
